import { useMemo, useState } from 'react';

import { AltynBankStatus } from '@/components/Shared/constants/banks-statuses';
import { BANKS_ICONS_NAMES, DictTypes } from '@/constants';
import { useWindowSize } from '@/hooks/useWindowSize';
import { useAppSelector } from '@/store';

import type { TBankOffers } from '../../../types/bank-offer.types';
import useAdditionalHelpers from './useAdditionalHelpers';
import useAlternatives from './useAlternatives';

type IProps = {
  bankOffer: TBankOffers;
  hasOfferedResponse?: boolean;
};

const blueColoredStatuses = [
  AltynBankStatus.WaitingOfFinancing,
  AltynBankStatus.MobileClient,
  AltynBankStatus.FinancedPledgeMissing,
  AltynBankStatus.FinancedWaitingForModificationBeforePledge,
  AltynBankStatus.OnRework
];

const useFormulateOfferItem = ({
  bankOffer: initialBankOffer,
  hasOfferedResponse
}: IProps) => {
  const [windowWidth] = useWindowSize();
  const isMobile = windowWidth <= 860;

  const { dicts } = useAppSelector((state) => state.dict);
  const { orderApp } = useAppSelector((state) => state.newOrder);

  const [offerDetailsModal, setOfferDetailsModal] = useState<boolean | string>(
    false
  );

  const { alternatives } = useAlternatives({ bankOffer: initialBankOffer });
  const bankOffer: TBankOffers = useMemo(() => {
    const product =
      orderApp?.productInfo.product.stocksBonds?.find(
        (prod) => prod.bank_name === initialBankOffer.bank.name
      )?.label || 'Стандарт';

    if (!alternatives || alternatives.length === 0) {
      return {
        ...initialBankOffer,
        product
      };
    }

    const hasMainInfo =
      initialBankOffer.monthlyPayment && initialBankOffer.rate;

    const selectedAlternative =
      alternatives.find(
        (item) => item.extUuid === initialBankOffer.offeredUuid
      ) || (!hasMainInfo ? alternatives[0] : undefined);
    const filteredAlternatives = alternatives.filter(
      (item) => item.extUuid !== selectedAlternative?.extUuid
    );

    let result = {
      ...initialBankOffer,
      alternatives: filteredAlternatives,
      product
    };
    if (selectedAlternative) {
      result = {
        ...result,
        rate: selectedAlternative.rate.toString(),
        extId: selectedAlternative.extUuid,
        initialPayment: selectedAlternative.initialPayment,
        loanAmount: selectedAlternative.loanAmount,
        loanPeriod: selectedAlternative.loanPeriod.toString(),
        monthlyPayment: selectedAlternative.monthlyPayment,
        product: selectedAlternative.product || product
      };
    }

    return result as TBankOffers;
  }, [
    initialBankOffer,
    alternatives,
    orderApp?.productInfo.product.stocksBonds
  ]);

  const { additionalProducts, getDescription } = useAdditionalHelpers({
    bankOffer,
    offerDetailsModal
  });

  const bankDict =
    dicts?.[DictTypes.Banks]?.getBy.name[initialBankOffer.bank.name];
  const bankIconName =
    BANKS_ICONS_NAMES[
      initialBankOffer.bank.name as keyof typeof BANKS_ICONS_NAMES
    ];
  const isCurrentResponseOffered =
    hasOfferedResponse && initialBankOffer.isOffered;

  return {
    additionalProducts,
    bankOffer,
    getDescription,
    headerIcon: bankDict?.icon || bankIconName?.icon,
    headerTitle: bankDict?.fullname || bankIconName?.name,
    isBlueColoredStatus: blueColoredStatuses.includes(
      initialBankOffer.status.code as AltynBankStatus
    ),
    isCurrentResponseOffered,
    isMobile,
    offerDetailsModal,
    setOfferDetailsModal
  };
};

export default useFormulateOfferItem;
